import { useState, useEffect, useRef } from 'react';
import { ExternalLink, Loader2, Globe, Image } from 'lucide-react';

type DisplayType = 'iframe' | 'image';

interface ServiceLinkPreviewProps {
  title: string;
  url: string;
  icon: string;
  description?: string;
  imageUrl?: string;
  displayType?: DisplayType;
  isDark: boolean;
}

// Get hostname from URL
const getHostname = (url: string): string => {
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
};

export function ServiceLinkPreview({ title, url, icon, description, imageUrl, displayType = 'iframe', isDark }: ServiceLinkPreviewProps) {
  const [loading, setLoading] = useState(true);
  const [iframeFailed, setIframeFailed] = useState(false);
  const [screenshotUrl, setScreenshotUrl] = useState<string | null>(null);
  const [showFallback, setShowFallback] = useState(false);
  const [imageError, setImageError] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const hostname = getHostname(url);

  // Determine if we should use custom image or fallback to iframe/screenshot
  const useCustomImage = displayType === 'image' && imageUrl && !imageError;

  // Pre-load screenshot in background (only if not using custom image)
  useEffect(() => {
    if (useCustomImage) return; // Skip screenshot if using custom image

    const screenshotApi = `https://api.microlink.io/?url=${encodeURIComponent(url)}&screenshot=true&meta=false&embed=screenshot.url`;

    fetch(screenshotApi)
      .then(res => res.json())
      .then(data => {
        if (data?.data?.screenshot?.url) {
          setScreenshotUrl(data.data.screenshot.url);
        }
      })
      .catch(() => {
        // Silently fail - will use fallback
      });
  }, [url, useCustomImage]);

  const handleIframeLoad = () => {
    setLoading(false);
    setIframeFailed(false);
  };

  const handleIframeError = () => {
    setLoading(false);
    setIframeFailed(true);
  };

  // Handle iframe timeout - show screenshot instead
  useEffect(() => {
    if (useCustomImage) return; // Skip timeout if using custom image

    const timeout = setTimeout(() => {
      if (loading && !iframeFailed) {
        setLoading(false);
        setIframeFailed(true);
      }
    }, 10000); // 10 second timeout

    return () => clearTimeout(timeout);
  }, [loading, iframeFailed, useCustomImage]);

  return (
    <div className={`rounded-lg overflow-hidden border ${isDark ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-gray-50'}`}>
      {/* Link Row */}
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className={`block ${isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-100'} transition-colors`}
      >
        <div className={`flex items-center gap-3 p-3`}>
          <span className="text-base flex-shrink-0">{icon}</span>
          <div className="flex-1 min-w-0">
            <span className={`text-sm font-medium block ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>{title}</span>
            {description && (
              <span className={`text-xs block mt-0.5 truncate ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{description}</span>
            )}
          </div>
          {useCustomImage && (
            <span className={`text-xs px-2 py-0.5 rounded flex-shrink-0 ${isDark ? 'bg-purple-900/50 text-purple-300' : 'bg-purple-100 text-purple-700'}`}>
              Image
            </span>
          )}
        </div>
        <ExternalLink className="w-4 h-4 opacity-40" />
      </a>

      {/* Preview Panel */}
      <div className={`border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
        {/* Custom Image Display */}
        {useCustomImage ? (
          <div className="relative group">
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="block"
            >
              <img
                src={imageUrl}
                alt={`Preview of ${title}`}
                className="w-full"
                loading="lazy"
                onError={() => setImageError(true)}
              />
              <div className={`absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-between p-3`}>
                <span className="text-white text-xs">{hostname}</span>
                <span className="text-white text-xs flex items-center gap-1">
                  <Image className="w-3 h-3" /> Visit
                </span>
              </div>
            </a>
          </div>
        ) : (
          <>
            {/* Initial loading state */}
            {loading && !iframeFailed && (
              <div className="flex items-center justify-center py-12">
                <Loader2 className={`w-6 h-6 animate-spin ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                <span className={`ml-2 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Loading preview...</span>
              </div>
            )}

            {/* Show iframe when it works */}
            {!loading && !iframeFailed && (
              <iframe
                ref={iframeRef}
                src={url}
                title={`Preview of ${title}`}
                className="w-full border-0"
                style={{ height: '300px' }}
                onLoad={handleIframeLoad}
                onError={handleIframeError}
                sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-presentation allow-downloads"
                referrerPolicy="no-referrer"
                loading="lazy"
              />
            )}

            {/* Show screenshot or fallback when iframe fails */}
            {iframeFailed && (
              <div>
                {screenshotUrl && !showFallback ? (
                  <div className="relative group">
                    <img
                      src={screenshotUrl}
                      alt={`Preview of ${title}`}
                      className="w-full"
                      loading="lazy"
                      onError={() => setShowFallback(true)}
                    />
                    <div className={`absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-between p-3`}>
                      <span className="text-white text-xs">{hostname}</span>
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-white text-xs bg-blue-600 px-2 py-1 rounded"
                      >
                        Visit
                      </a>
                    </div>
                  </div>
                ) : (
                  /* Fallback card */
                  <div className={`p-4 ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
                    <div className="flex items-start gap-3">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
                        <Globe className={`w-6 h-6 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                      </div>
                      <div className="flex-1">
                        <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{title}</p>
                        <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{hostname}</p>
                        <p className={`text-xs mt-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                          Site doesn't allow preview
                        </p>
                      </div>
                    </div>
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`mt-3 block text-center text-sm py-2 rounded ${isDark ? 'bg-blue-600 text-white hover:bg-blue-500' : 'bg-blue-600 text-white hover:bg-blue-500'}`}
                    >
                      Visit Website
                    </a>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* URL Display */}
        <div className={`px-3 py-2 text-xs ${isDark ? 'text-gray-500 bg-gray-800/50' : 'text-gray-400 bg-gray-100'} border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          {url}
        </div>
      </div>
    </div>
  );
}
